import * as React from 'react';
import { Box, Flex } from 'components/ui';
import { useWallet } from 'components/WalletContext';
import { useApprovalEnigma, useApprovalGenesis, useStaking } from 'hooks';
import StakeRow from './StakeRow';
import StakingStats from './StakingStats';
import { useQueryClient } from 'react-query';

interface StakeListProps {}

const Separator: React.FC<any> = ({ text, ...rest }) => {
  return (
    <Box
      background="linear-gradient(to right, rgba(255, 223, 109, 1), rgba(255, 223, 109, 0))"
      borderTopRightRadius="2"
      borderTopLeftRadius="2"
      color="black"
      p="2"
      width="100%"
      {...rest}
    >
      {text}
    </Box>
  );
};

const StakeList: React.FC<StakeListProps> = () => {
  const { address } = useWallet();
  const {
    totalStaked,
    pendingRewards,
    claimReward,
    portfolioPairs,
    pairsWithBalances,
    maxPairs,
  } = useStaking();

  const {
    approve: approveEnigma,
    approved: enigmaApproved,
    checked: enigmaChecked,
    approving: approvingEnigma,
  } = useApprovalEnigma();

  const {
    approve: approveGenesis,
    approved: genesisApproved,
    checked: genesisChecked,
    approving: approvingGenesis,
  } = useApprovalGenesis();

  const queryClient = useQueryClient();

  async function claim() {
    return claimReward(() => {
      console.log('Claimed ETH reward');
      queryClient.invalidateQueries('pendingRewards');
    });
  }

  const capReached = !maxPairs ? false : (totalStaked || 0) >= maxPairs;
  const ownsPairs = address && portfolioPairs && portfolioPairs.length > 0;
  const noPairs = address && genesisChecked && enigmaChecked && portfolioPairs.length === 0;

  return (
    <Flex width="100%" column>
      <StakingStats
        approveEnigma={approveEnigma}
        approvingEnigma={approvingEnigma}
        enigmaApproved={enigmaApproved}
        approveGenesis={approveGenesis}
        approvingGenesis={approvingGenesis}
        genesisApproved={genesisApproved}
        totalStaked={totalStaked}
        enigmaChecked={enigmaChecked}
        genesisChecked={genesisChecked}
        pendingRewards={pendingRewards}
        claim={claim}
        maxPairs={maxPairs || 0}
        capReached={capReached}
      />

      <Separator text="My pairs" />

      {ownsPairs && portfolioPairs.map((pair, index) => (
        <StakeRow
          genesisId={pair.genesisId}
          enigmaId={pair.enigmaId}
          pairId={pair.pairId}
          balance={pair.balance || { enigma: 0, genesis: 0, pair: 0, staked: 0 }}
          p={['4', '2']}
          key={pair.pairId}
          isApproved={genesisApproved && enigmaApproved}
          index={index}
          capReached={capReached}
        />
      ))}

      {noPairs && (
        <Flex
          p={['4', '2']}
          width="100%"
          background="rgba(0, 0, 0, 0.1)"
        >
          You do not own any pairs.
        </Flex>
      )}

      <Separator text="All pairs" mt="4" />

      <Flex width="100%" wrap>
        {pairsWithBalances.map((pair, index) => (
          <StakeRow
            genesisId={pair.genesisId}
            enigmaId={pair.enigmaId}
            pairId={pair.pairId}
            balance={pair.balance || { enigma: 0, genesis: 0, pair: 0, staked: 0 }}
            p={['4', '2']}
            key={pair.pairId}
            isApproved={genesisApproved && enigmaApproved}
            index={index}
            readOnly
            width={['100%', '100%', '50%']}
          />
        ))}
      </Flex>
    </Flex>
  );
};

export default StakeList;
